import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AnimatedCheck from '../components/AnimatedCheck';
import AnimatedMail from '../components/AnimatedMail';
import AnimatedError from '../components/AnimatedError';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, ArrowLeft, Eye, EyeOff, Check } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';
import {
  cleanPhone,
  validateName,
  validateEmail,
  validateWhatsapp,
  validatePassword,
  validateConfirmPassword,
} from '../lib/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------- Component ----------
export default function SignupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const planFromUrl = params.get('plan') || '';
  const sessionId = params.get('session_id') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    whatsapp: '+55',
    plan_id: planFromUrl,
  });
  const [touched, setTouched] = useState({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [checkoutCancel, setCheckoutCancel] = useState(false);
  const [plan, setPlan] = useState(null); // selected plan loaded from CMS

  // Load plan features from CMS
  useEffect(() => {
    if (!planFromUrl) return;
    let cancelled = false;
    fetch(`${API_URL}/api/cms`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const plans = data?.pricing?.plans || [];
        const found = plans.find((p) => p.name === planFromUrl);
        if (found) setPlan(found);
      })
      .catch(() => {
        /* silent — plan card simply won't render */
      });
    return () => { cancelled = true; };
  }, [planFromUrl]);

  useEffect(() => {
    if (location.pathname === '/signup/success' && sessionId) setCheckoutSuccess(true);
    if (location.pathname === '/signup/cancel') setCheckoutCancel(true);
  }, [location, sessionId]);

  // Live validation results
  const errors = useMemo(() => ({
    name: validateName(formData.name),
    email: validateEmail(formData.email),
    whatsapp: validateWhatsapp(formData.whatsapp),
    password: validatePassword(formData.password),
    confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword),
  }), [formData]);

  const isValid = !errors.name && !errors.email && !errors.whatsapp && !errors.password && !errors.confirmPassword && acceptTerms;

  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const showError = (field) => touched[field] && errors[field];

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark everything touched so all errors show
    setTouched({ name: true, email: true, whatsapp: true, password: true, confirmPassword: true });
    if (!isValid) {
      toast.error('Corrija os campos destacados antes de continuar.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          whatsapp: cleanPhone(formData.whatsapp),
          plan_id: formData.plan_id,
          host_url: window.location.origin,
          accept_terms: acceptTerms,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        // Sem toast aqui: a própria tela já dá o recado, e o aviso verde de sucesso
        // competia com o título, reforçando a leitura de "acabou" que a gente quis desfazer.
        setSuccess(true);
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao cadastrar'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Checkout/success screens (unchanged) ----
  if (checkoutSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedCheck />
            <h2 className="text-2xl font-bold mb-2">Pagamento Realizado!</h2>
            <p className="text-muted-foreground mb-4">
              Seu pagamento foi processado com sucesso. Seu plano será ativado em até <strong>24 horas</strong>.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Você receberá um email confirmando a ativação da sua conta.
            </p>
            <Button onClick={() => navigate('/login')}>Ir para Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkoutCancel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedError />
            <h2 className="text-2xl font-bold mb-2">Pagamento Cancelado</h2>
            <p className="text-muted-foreground mb-6">
              O pagamento não foi concluído. Você pode tentar novamente quando quiser.
            </p>
            <Button onClick={() => navigate('/')}>Voltar à Página Inicial</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    // Nada de check verde nem de "Cadastro Realizado": os dois anunciam tarefa concluída, e o
    // usuário parava de ler ali, clicava no único botão da tela (que era "Ir para Login") e
    // esbarrava no acesso bloqueado sem nunca abrir o e-mail. Agora a tela anuncia PENDÊNCIA,
    // o passo a passo vem numerado e o botão principal leva para a caixa de entrada.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedMail />
            <h2 className="text-2xl font-bold mb-2">Confirme seu e-mail</h2>
            <p className="text-muted-foreground mb-5">
              Enviamos um link de confirmação para <strong>{formData.email}</strong>.
              Sua conta só é ativada depois que você clicar nesse link.
            </p>
            <ol className="text-left text-sm space-y-3 mb-4 rounded-lg border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
              {[
                <>Abra sua caixa de e-mail.</>,
                <>Na mensagem do Watizap, clique no botão <strong>Confirmar Email</strong>.</>,
                <>Pronto: sua conta é ativada e você já pode entrar.</>,
              ].map((texto, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="text-amber-950 dark:text-amber-100">{texto}</span>
                </li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground mb-6">
              Não encontrou a mensagem? Procure na caixa de spam ou no lixo eletrônico.
            </p>
            {/* Único botão da tela, e a única ação que o Watizap realmente executa daqui.
                Sem atalho para webmail: adivinhar o provedor pelo domínio deixava o botão
                principal existindo para uns e sumindo para outros, e mandava a pessoa para
                uma tela de login do provedor quando ela lê e-mail no aplicativo. */}
            <Button
              variant="outline"
              className="w-full bg-transparent border-amber-400 text-amber-900 hover:bg-amber-600 hover:text-white hover:border-amber-600 dark:border-amber-900/60 dark:text-amber-200 dark:hover:bg-amber-700 dark:hover:text-white"
              onClick={() => navigate('/login')}
            >
              Já confirmei, ir para o login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Form ----
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Seo
        title="Criar conta | Watizap"
        description="Crie sua conta no Watizap e comece a enviar mensagens em massa pelo WhatsApp. Teste grátis."
        path="/signup"
      />
      <Card className="max-w-md w-full">
        <CardHeader>
          <Button variant="outline" size="sm" className="w-fit mb-2" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <CardTitle>Criar Conta</CardTitle>
          <CardDescription>
            {planFromUrl ? `Plano selecionado: ${planFromUrl}` : 'Preencha seus dados para criar sua conta'}
          </CardDescription>

          {/* Plan features card (from CMS) */}
          {plan && (
            <div
              className="mt-4 rounded-lg border border-teal-200 bg-teal-50/60 p-4"
              data-testid="signup-plan-features"
            >
              {plan.description && (
                <p className="text-sm text-teal-900 font-medium mb-3">{plan.description}</p>
              )}
              {Array.isArray(plan.features) && plan.features.length > 0 && (
                <ul className="space-y-1.5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-teal-900">
                      <Check className="w-4 h-4 mt-0.5 text-teal-600 flex-shrink-0" />
                      <span title={f.tooltip || ''}>{f.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onBlur={() => markTouched('name')}
                placeholder="Ex.: Maria da Silva"
                aria-invalid={!!showError('name')}
                className={showError('name') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-name"
              />
              {showError('name') && (
                <p className="text-xs text-red-600" data-testid="signup-name-error">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={() => markTouched('email')}
                placeholder="seu@email.com"
                aria-invalid={!!showError('email')}
                className={showError('email') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-email"
              />
              {showError('email') && (
                <p className="text-xs text-red-600" data-testid="signup-email-error">{errors.email}</p>
              )}
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <Label htmlFor="whatsapp">
                WhatsApp <span className="text-xs text-muted-foreground font-normal">(formato internacional)</span>
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                inputMode="tel"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                onBlur={() => markTouched('whatsapp')}
                placeholder="+5511999999999"
                aria-invalid={!!showError('whatsapp')}
                className={showError('whatsapp') ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="signup-whatsapp"
              />
              {showError('whatsapp') ? (
                <p className="text-xs text-red-600" data-testid="signup-whatsapp-error">{errors.whatsapp}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ex.: <strong className="font-mono font-bold text-foreground">+5511999999999</strong> (código do país + DDD + número).
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  onBlur={() => markTouched('password')}
                  placeholder="Mínimo 8 caracteres, com letra e número"
                  aria-invalid={!!showError('password')}
                  className={(showError('password') ? 'border-red-500 focus-visible:ring-red-500 ' : '') + 'pr-10'}
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {showError('password') && (
                <p className="text-xs text-red-600" data-testid="signup-password-error">{errors.password}</p>
              )}
            </div>

            {/* Confirm password */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  onBlur={() => markTouched('confirmPassword')}
                  placeholder="Digite a senha novamente"
                  aria-invalid={!!showError('confirmPassword')}
                  className={(showError('confirmPassword') ? 'border-red-500 focus-visible:ring-red-500 ' : '') + 'pr-10'}
                  data-testid="signup-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {showError('confirmPassword') && (
                <p className="text-xs text-red-600" data-testid="signup-confirm-password-error">{errors.confirmPassword}</p>
              )}
              {!showError('confirmPassword') && formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="text-xs text-teal-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Senhas conferem
                </p>
              )}
            </div>

            <div className="pt-2">
              {/* Consent checkbox (LGPD) */}
              <label className="flex items-start gap-3 mb-4 cursor-pointer select-none" data-testid="signup-consent-wrapper">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                  data-testid="signup-consent-checkbox"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  Li e aceito a{' '}
                  <a
                    href="/politica-de-privacidade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline font-medium"
                    data-testid="signup-link-privacy"
                  >
                    Política de Privacidade
                  </a>{' '}
                  e os{' '}
                  <a
                    href="/termos-de-uso"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline font-medium"
                    data-testid="signup-link-terms"
                  >
                    Termos de Uso
                  </a>
                  .
                </span>
              </label>
              <p className="text-xs text-muted-foreground mb-4">
                Após o cadastro, enviaremos um link de confirmação para o seu email. Ao clicar nele, sua conta será ativada automaticamente.
              </p>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !isValid}
                data-testid="signup-submit"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Conta
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
